import { useEffect } from 'react';
import { Platform, StyleSheet, View, type ViewStyle } from 'react-native';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
  withRepeat,
  withSequence,
  withTiming,
  withDelay,
  Easing,
} from 'react-native-reanimated';

type ElectricBorderProps = {
  children: React.ReactNode;
  style?: ViewStyle;
  color?: string;
};

export default function ElectricBorder({ children, style, color = '#22D3EE' }: ElectricBorderProps) {
  const phase = useSharedValue(0);
  const flicker = useSharedValue(1);
  const mountY = useSharedValue(8);
  const mountOpacity = useSharedValue(0);

  const hex = color.replace('#', '');
  const r = parseInt(hex.slice(0, 2), 16) || 34;
  const g = parseInt(hex.slice(2, 4), 16) || 211;
  const b = parseInt(hex.slice(4, 6), 16) || 238;

  useEffect(() => {
    mountOpacity.value = withTiming(1, { duration: 500, easing: Easing.out(Easing.cubic) });
    mountY.value = withTiming(0, { duration: 500, easing: Easing.out(Easing.cubic) });

    phase.value = withRepeat(
      withSequence(
        withTiming(1, { duration: 900, easing: Easing.inOut(Easing.sin) }),
        withTiming(0.2, { duration: 600, easing: Easing.inOut(Easing.sin) }),
        withTiming(0.8, { duration: 700, easing: Easing.inOut(Easing.sin) }),
        withTiming(0.1, { duration: 500, easing: Easing.inOut(Easing.sin) }),
      ),
      -1,
      false,
    );

    flicker.value = withDelay(
      400,
      withRepeat(
        withSequence(
          withTiming(1, { duration: 80 }),
          withTiming(0.6, { duration: 60 }),
          withTiming(1, { duration: 80 }),
          withTiming(0.8, { duration: 120 }),
          withTiming(1, { duration: 2200 }),
        ),
        -1,
        false,
      ),
    );
  }, []);

  const mountStyle = useAnimatedStyle(() => ({
    opacity: mountOpacity.value,
    transform: [{ translateY: mountY.value }],
  }));

  const borderStyle = useAnimatedStyle(() => {
    const alpha = phase.value * flicker.value;
    const spread = 8 + phase.value * 16;
    if (Platform.OS !== 'web') {
      return { borderColor: `rgba(${r},${g},${b},${alpha})` };
    }
    return {
      borderColor: `rgba(${r},${g},${b},${alpha})`,
      boxShadow: `0 0 ${spread}px rgba(${r},${g},${b},${alpha * 0.6}), 0 0 ${spread * 2}px rgba(${r},${g},${b},${alpha * 0.2})`,
    } as any;
  });

  return (
    <Animated.View style={[mountStyle, { borderRadius: 18 }]}>
      <Animated.View style={[s.shell, borderStyle, style]}>
        <View style={s.inner}>{children}</View>
      </Animated.View>
    </Animated.View>
  );
}

const s = StyleSheet.create({
  shell: {
    borderRadius: 18,
    borderWidth: 1.4,
    padding: 1,
  },
  inner: { borderRadius: 17, overflow: 'hidden' },
});
